import { useRecordContext } from "ra-core";
import { Badge } from "@/components/ui/badge";
import type { Contact } from "../types";
import { channelFromRawSource, channelLabel } from "../echoflicks/attribution";

/**
 * First-touch acquisition (set-once dashboard projection): which ad or website
 * campaign brought this contact in. Organic and Unknown are explicit states.
 */
export const ContactAcquisitionInfo = () => {
  const record = useRecordContext<Contact>();
  if (!record) return null;

  const detail = record.acquisition_detail ?? {};
  const parsed = channelFromRawSource(record.acquisition_source);
  const group =
    record.acquisition_group ??
    (!record.acquisition_source || record.acquisition_source === "organic"
      ? "organic"
      : "unknown");
  const observedAt = record.acquisition_at ?? record.first_seen ?? null;
  const headline = typeof detail.headline === "string" ? detail.headline : null;
  const adId = typeof detail.source_id === "string" ? detail.source_id : null;
  const token =
    (typeof detail.token === "string" ? detail.token : null) ?? parsed.token;
  const booth = typeof detail.booth === "string" ? detail.booth : null;
  // Launchpad stamps the Meta UTMs into acquisition_detail; the campaign is
  // the operator-meaningful grouping (matches the Funnel tab's by-campaign).
  const utmCampaign =
    typeof detail.utm_campaign === "string" && detail.utm_campaign
      ? detail.utm_campaign
      : null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Badge variant="secondary">
          {group === "paid"
            ? "Paid"
            : group === "organic"
              ? "Organic"
              : "Unknown"}
        </Badge>
        <Badge variant="outline">{channelLabel(parsed.channel)}</Badge>
        {observedAt ? (
          <span className="text-xs text-muted-foreground">
            {new Date(observedAt).toLocaleDateString()}
          </span>
        ) : null}
      </div>
      {booth ? (
        <span className="text-xs text-muted-foreground">Booth: {booth}</span>
      ) : null}
      {utmCampaign ? (
        <span className="text-xs text-muted-foreground">
          Campaign: {utmCampaign}
        </span>
      ) : null}
      <span className="text-xs text-muted-foreground">
        {[
          record.acquisition_medium,
          record.acquisition_platform,
          record.acquisition_surface,
        ]
          .filter(Boolean)
          .join(" · ") || "Direct WhatsApp"}
      </span>
      {record.acquisition_confidence ? (
        <span className="text-xs text-muted-foreground">
          Confidence: {record.acquisition_confidence.replaceAll("_", " ")}
        </span>
      ) : null}
      {token ? (
        <span className="text-xs text-muted-foreground">
          Campaign token: {token}
        </span>
      ) : null}
      {headline ? (
        <span className="text-xs text-muted-foreground line-clamp-2">
          “{headline}”
        </span>
      ) : null}
      {adId ? (
        <span className="text-xs text-muted-foreground">Ad ID: {adId}</span>
      ) : null}
    </div>
  );
};
